import type { IRItemCard, IRItemInfo } from '../types';

import { IRMetricKey } from '../types';

export const GENERAL_METRIC_INFO: IRItemCard = {
  title: 'Metrics',
  content:
    'Counts and calculations — like the number of customers you have or your total order — that help you evaluate the impact of each variant from your experiment. Metrics show up as the columns of your data table.',
  helps: [],
};
export const REPORT_METRIC_INFO: IRItemInfo = {
  [IRMetricKey.SESSION]: {
    title: 'Session',
    content: 'A period during which a visitor interacts with your online store from experiment',
    helps: [
      'Track how many times visitors browse your online store for each variant',
      'Measure the effectiveness of your marketing campaigns for each variant',
    ],
  },
  [IRMetricKey.ORDERS]: {
    title: 'Orders',
    content: 'Number of orders generated from your experiment',
    helps: [
      'Track the number of orders for each variant',
      'Compare order frequency across each variant',
    ],
  },
  [IRMetricKey.PAGE_VIEWS]: {
    title: 'Pageviews',
    content: 'Number of times a page from your experiment has been viewed by visitors',
    helps: [
      'Track how much content visitors view on your online store for each variant',
      'See which variant attracts the most visitor attention',
    ],
  },
  [IRMetricKey.VISITORS]: {
    title: 'Visitors',
    content: 'Number of unique individuals who visit your experiment',
    helps: [
      'Track how many different people discover each variant',
      'Measure your reach versus repeat visits for each variant',
    ],
  },
  [IRMetricKey.BOUNCE_RATE]: {
    title: 'Bounce rate',
    content:
      'Percentage of visitors who leave your store after viewing only one page, without interacting further from the experiment',
    helps: ['Measure how well each variant keeps visitors engages'],
    formula: (
      <>
        <span className="formula-variable">Bounce rate</span>
        <span> = </span>
        <span className="formula-input">sessions with a pageview</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total sessions</span>
      </>
    ),
  },
  [IRMetricKey.CTR]: {
    title: 'Click-through rate',
    content:
      'Percentage of clicks to open new page, compared to the number of times the page was viewed from the experiment',
    helps: ['Measure how many people go through the next step for each variant'],
    formula: (
      <>
        <span className="formula-variable">Click-through rate</span>
        <span> = </span>
        <span className="formula-input">total clicks</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total pageviews</span>
      </>
    ),
  },
  [IRMetricKey.CONVERSION_RATE]: {
    title: 'Conversion rate',
    content: 'Percentage of online store sessions that completed an order from the experiment',
    helps: [
      'Track how well each variant turns browsers into buyers',
      'Set benchmarks to track and compare how well each variant converts visitors into customers.',
    ],
    formula: (
      <>
        <span className="formula-variable">Conversion rate</span>
        <span> = </span>
        <span className="formula-input">sessions that completed checkout</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total sessions</span>
      </>
    ),
  },
  [IRMetricKey.AVG_TIME_ON_PAGE]: {
    title: 'Average time on page',
    content:
      'Average duration that visitors spend on a page before clicking to open new page from the experiment',
    helps: ['Compare visit length across different time periods for each variant'],
    formula: (
      <>
        <span className="formula-variable">Average time on page</span>
        <span> = </span>
        <span className="formula-input">total time on page</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">(total pageviews - total exits)</span>
      </>
    ),
  },
  [IRMetricKey.ADDED_TO_CART]: {
    title: 'Added to cart',
    content:
      'Total number of the event when a customer adds a product to their cart from the experiment',
    helps: [
      'Track how many visitors in each variant shows serious buying interest',
      'Improve product pages if too few visitors add items to cart',
    ],
  },
  [IRMetricKey.ADD_TO_CART_RATE]: {
    title: 'Add to cart rate',
    content:
      'Percentage of sessions in which the customer added item to the cart from the experiment',
    helps: [
      'Track how many visitors in each variant show serious buying interest',
      'Improve product pages if too few visitors add items to cart',
    ],
    formula: (
      <>
        <span className="formula-variable">Added to cart rate</span>
        <span> = </span>
        <span className="formula-input">sessions with cart additions</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total sessions</span>
      </>
    ),
  },
  [IRMetricKey.REACHED_CHECKOUT]: {
    title: 'Session that reached check-out',
    content:
      'Sessions in your online store in which the checkout page was reached from the experiment',
    helps: [
      'See how many visitors in each variant make it to your checkout page',
      'Fix checkout issues if too many visitors abandon at this step',
    ],
  },
  [IRMetricKey.COMPLETE_CHECKOUT]: {
    title: 'Session that completed check-out',
    content: 'Sessions in your online store in which a purchase was completed from the experiment',
    helps: ['Track successful sales in each variant from browsing visits'],
  },
  [IRMetricKey.CART_ADDITION]: {
    title: 'Sessions with cart additions',
    content:
      'Sessions in your online store in which a visitor added item to the cart from the experiment',
    helps: ['Track how many visitors in each variant show serious buying interest'],
  },
  [IRMetricKey.AOV]: {
    title: 'Average order value',
    content: 'Average value of orders placed in your online store from the experiment',
    helps: [
      'Increase revenue by focusing on strategies that raise average order value',
      'Set minimum order thresholds for free shipping and promotions',
    ],
    formula: (
      <>
        <span className="formula-variable">Average order value</span>
        <span> = </span>
        <span className="formula-input">revenue</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total orders</span>
      </>
    ),
  },
  [IRMetricKey.RPV]: {
    title: 'Revenue per visitor',
    content: 'Average revenue generated from each visitor from the experiment',
    helps: [
      'Track customer lifetime value to inform acquisition and retention spending',
      'Identify your highest-value customers for VIP treatment and retention focus',
    ],
    formula: (
      <>
        <span className="formula-variable">Revenue per visitor</span>
        <span> = </span>
        <span className="formula-input">total revenue</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total visitors</span>
      </>
    ),
  },
  [IRMetricKey.REVENUE]: {
    title: 'Revenue',
    content: 'Total income generated from sales after discounts from the experiment',
    helps: [
      'Track your revenue potential before any adjustments in each variant',
      'Compare full-price performance across products and time periods in each variant',
    ],
    formula: (
      <>
        <span className="formula-variable">Revenue</span>
        <span> = </span>
        <span className="formula-input">gross sales</span>
        <span className="formula-operator"> - </span>
        <span className="formula-input">discounts</span>
      </>
    ),
  },
};
